import { useEffect } from "react";
import {
  useCDIAlertsV2,
  useMyQueriesOpenCount,
  useOpcdHimAlertsCount,
  useOpcdQueriesPendingCount,
  useOpcdCodingAlertsCount,
  useUmQueriesOpenCount,
  useUnreadConversationCount,
  useVBCAlerts,
} from "./queries";
import { setMessageCount } from "../store/messageReducer";
import {
  setCDICount,
  setMyQueriesCount,
  setOpcdHimAlertsCount,
  setOpcdQueriesCount,
  setOpcdCodingAlertsCount,
  setUmQueriesCount,
  setVBCCount,
} from "../store/providerReducer";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  extractCDIAlertsV2,
  normalizeCDIAlertStatus,
} from "../utils/cdi-alert-utils";
import {
  buildVBCAlertRows,
  countVisibleVBCAlertRows,
  extractVBCAlertsV2,
} from "../utils/vbc-alert-utils";

export const useHomeModuleAlertCounts = () => {
  const dispatch = useAppDispatch();
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);
  const mrn = useAppSelector((state) => state?.fhir?.mrn);

  const { data: messageCount } = useUnreadConversationCount(providerNPI, {
    polling: true,
    enabled: Boolean(providerNPI),
  });

  const { data: openQueriesCount } = useMyQueriesOpenCount(providerNPI, {
    polling: true,
    enabled: Boolean(providerNPI),
  });

  const { data: umOpenQueriesCount } = useUmQueriesOpenCount(providerNPI, {
    polling: true,
    enabled: Boolean(providerNPI),
  });

  const { data: opcdQueriesCount } = useOpcdQueriesPendingCount(providerNPI, {
    polling: true,
    enabled: Boolean(providerNPI),
  });

  const { data: opcdHimAlertsCount } = useOpcdHimAlertsCount(providerNPI, {
    polling: true,
    enabled: Boolean(providerNPI),
  });

  const { data: opcdCodingAlertsCount } = useOpcdCodingAlertsCount({
    polling: true,
    enabled: Boolean(providerNPI),
  });

  const { data: vbcResponse } = useVBCAlerts(mrn ?? undefined, {
    polling: true,
    enabled: Boolean(mrn),
  });

  const { data: cdiResponse } = useCDIAlertsV2(
    { mrn_no: mrn ?? "", status_filter: null },
    { polling: true, enabled: Boolean(mrn) }
  );

  useEffect(() => {
    if (messageCount !== undefined) {
      dispatch(setMessageCount(messageCount));
    }
  }, [dispatch, messageCount]);

  useEffect(() => {
    if (openQueriesCount !== undefined) {
      dispatch(setMyQueriesCount(openQueriesCount));
    }
  }, [dispatch, openQueriesCount]);

  useEffect(() => {
    if (umOpenQueriesCount !== undefined) {
      dispatch(setUmQueriesCount(umOpenQueriesCount));
    }
  }, [dispatch, umOpenQueriesCount]);

  useEffect(() => {
    if (opcdQueriesCount !== undefined) {
      dispatch(setOpcdQueriesCount(opcdQueriesCount));
    }
  }, [dispatch, opcdQueriesCount]);

  useEffect(() => {
    if (opcdHimAlertsCount !== undefined) {
      dispatch(setOpcdHimAlertsCount(opcdHimAlertsCount));
    }
  }, [dispatch, opcdHimAlertsCount]);

  useEffect(() => {
    if (opcdCodingAlertsCount !== undefined) {
      dispatch(setOpcdCodingAlertsCount(opcdCodingAlertsCount));
    }
  }, [dispatch, opcdCodingAlertsCount]);

  useEffect(() => {
    if (!mrn) return;

    if (vbcResponse?.status === 200) {
      const vbcAlerts = buildVBCAlertRows(extractVBCAlertsV2(vbcResponse.data));
      dispatch(setVBCCount(countVisibleVBCAlertRows(vbcAlerts)));
    }
  }, [dispatch, mrn, vbcResponse]);

  useEffect(() => {
    if (!mrn) return;

    if (cdiResponse?.status === 200) {
      const cdiAlerts = extractCDIAlertsV2(cdiResponse.data);
      dispatch(
        setCDICount(
          cdiAlerts.filter(
            (alert) => normalizeCDIAlertStatus(alert.status) === "PENDING"
          ).length
        )
      );
    }
  }, [cdiResponse, dispatch, mrn]);
};
